import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getGroupedRowModel,
    getExpandedRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
    type VisibilityState,
    type GroupingState,
    type ExpandedState,
    type RowSelectionState,
    type Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { IconClipboardList } from '@tabler/icons-react';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { DataTableToolbar } from './DataTableToolbar';
import { DataTableColumnHeader } from './DataTableColumnHeader';
import { useDataTableFilters } from './hooks/useDataTableFilters';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import type { DataTableProps, DataTableColumnMeta, DataTableFiltersApi, ServerFilterVariables } from './types';
import { Loader, LoaderModeType } from '../Loader';

function DataTableLoadingOverlay() {
    return (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/55 backdrop-blur-[1px]">
            <Loader mode={LoaderModeType.Spin} />
        </div>
    );
}

export type { DataTableProps, DataTableColumnMeta, DataTableFiltersApi, ServerFilterVariables };
export { DataTableColumnHeader };
export {
    textColumn,
    booleanColumn,
    multiSelectColumn,
    dateColumn,
    dateRangeColumn,
    numberColumn,
    numberRangeColumn,
} from './columnHelpers';

export function DataTable<TData, TFilter extends Record<string, unknown> = Record<string, unknown>>({
    columns,
    data,
    filterMode,
    getRowId,
    getRowLink,
    onRowClick,
    refetch,
    fetchMore,
    hasMore,
    defaultFilters,
    pinnedFilters,
    defaultSortBy,
    defaultSortOrder,
    searchPlaceholder,
    searchFilterKey,
    autoFocusSearch = true,
    enableSelection = false,
    selectedKeys,
    onSelectionChange,
    highlightedKeys,
    isLoading = false,
    disableColumnVisibility = false,
    disableUrlSync = false,
    toolbarActions,
    scrollableBanner,
    emptyMessage = 'Aucun résultat.',
    groupBy,
    renderGroupRow,
    getRowClassName,
    renderRowDetails,
    pinnedSort,
    expanded: controlledExpanded,
    onExpandedChange: controlledOnExpandedChange,
    enableLazyTree = false,
    getRowCanExpand: getRowCanExpandProp,
    loadChildren,
    enableVirtualization = false,
    estimatedRowHeight = 44,
    extraFilters,
    onFiltersChange,
    filtersApiRef,
}: DataTableProps<TData, TFilter>) {
    const navigate = useNavigate();

    // Sorting state
    const [userSorting, setUserSorting] = useState<SortingState>(() =>
        defaultSortBy ? [{ id: defaultSortBy, desc: defaultSortOrder === 'desc' }] : [],
    );
    const pinnedIds = useMemo(() => new Set((pinnedSort ?? []).map((s) => s.id)), [pinnedSort]);
    const sorting = useMemo(() => [...(pinnedSort ?? []), ...userSorting], [pinnedSort, userSorting]);
    const handleSortingChange = useCallback(
        (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
            const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
            setUserSorting(next.filter((s) => !pinnedIds.has(s.id)));
        },
        [sorting, pinnedIds],
    );

    const groupingKey = (groupBy ?? []).join(',');
    const [grouping, setGrouping] = useState<GroupingState>(groupBy ?? []);
    useEffect(() => {
        setGrouping(groupingKey ? groupingKey.split(',') : []);
    }, [groupingKey]);

    const [internalExpanded, setInternalExpanded] = useState<ExpandedState>(groupingKey ? true : {});
    const expanded = controlledExpanded ?? internalExpanded;
    const setExpanded = useCallback(
        (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
            const next = typeof updater === 'function' ? updater(expanded) : updater;
            if (controlledOnExpandedChange) {
                controlledOnExpandedChange(next);
            } else {
                setInternalExpanded(next);
            }
        },
        [expanded, controlledOnExpandedChange],
    );

    // Lazy tree: children are fetched on first expand and merged into the data handed to the table.
    const [childrenByRowId, setChildrenByRowId] = useState<Map<string, TData[]>>(new Map());
    const [loadingRowIds, setLoadingRowIds] = useState<Set<string>>(new Set());

    const rootById = useMemo(() => {
        const map = new Map<string, TData>();
        for (const root of data) map.set(getRowId(root), root);
        return map;
    }, [data, getRowId]);

    const tableData = useMemo(() => {
        if (!enableLazyTree) return data;
        return data.map((root) => {
            // Children provided inline by the parent (e.g. a pre-built filtered subtree) win over the lazy cache,
            // which may still hold a previously-loaded full child list for the same row id.
            if ((root as { subRows?: TData[] }).subRows) return root;
            const children = childrenByRowId.get(getRowId(root));
            return children ? { ...root, subRows: children } : root;
        });
    }, [enableLazyTree, data, childrenByRowId, getRowId]);

    useEffect(() => {
        if (!enableLazyTree || !loadChildren || expanded === true) return;
        const expandedIds = Object.keys(expanded).filter((id) => (expanded as Record<string, boolean>)[id]);
        for (const id of expandedIds) {
            if (childrenByRowId.has(id) || loadingRowIds.has(id)) continue;
            const root = rootById.get(id);
            // Skip rows whose children are already provided inline (e.g. a pre-built, pre-expanded filtered tree).
            if (!root || (root as { subRows?: TData[] }).subRows) continue;
            setLoadingRowIds((prev) => new Set(prev).add(id));
            loadChildren(root)
                .then((children) => setChildrenByRowId((prev) => new Map(prev).set(id, children)))
                .catch(() => setChildrenByRowId((prev) => new Map(prev).set(id, [])))
                .finally(() =>
                    setLoadingRowIds((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    }),
                );
        }
    }, [enableLazyTree, loadChildren, expanded, childrenByRowId, loadingRowIds, rootById]);

    // Column visibility state (init from meta.defaultVisible)
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
        const vis: VisibilityState = {};
        for (const col of columns) {
            const meta = col.meta as DataTableColumnMeta<TFilter> | undefined;
            if (meta?.defaultVisible === false) {
                const id = 'accessorKey' in col ? String(col.accessorKey) : 'id' in col ? String(col.id) : '';
                if (id) vis[id] = false;
            }
        }
        return vis;
    });

    // Filters
    const {
        filterState,
        serverFilters,
        searchValue,
        updateSearch,
        updateFilter,
        clearFilters,
        columnFiltersState,
        globalFilter,
        debouncedRefetch,
    } = useDataTableFilters<TData, TFilter>({
        columns,
        filterMode,
        enableUrlSync: !disableUrlSync,
        defaultFilters,
        pinnedFilters,
        searchFilterKey,
        refetch,
        extraFilters,
    });

    // Surface filter-state changes to the parent (e.g. to drive a server-backed extra filter).
    useEffect(() => {
        onFiltersChange?.(filterState);
    }, [filterState, onFiltersChange]);

    // Imperative filter handle for the parent (e.g. summary cards toggling a preset filter).
    useEffect(() => {
        if (!filtersApiRef) return;
        filtersApiRef.current = { setFilter: updateFilter, clearFilters };
        return () => {
            filtersApiRef.current = null;
        };
    }, [filtersApiRef, updateFilter, clearFilters]);

    // Server-side: trigger refetch on filter/sort changes (skip initial mount — the parent query already fetches)
    const isInitialRefetchSkipRef = useRef(true);
    useEffect(() => {
        if (isInitialRefetchSkipRef.current) {
            isInitialRefetchSkipRef.current = false;
            return;
        }
        if (filterMode === 'server' && debouncedRefetch) {
            const sortBy = sorting[0]?.id;
            const sortOrder = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;
            debouncedRefetch(sortBy, sortOrder, serverFilters);
        }
    }, [serverFilters, sorting, filterMode, debouncedRefetch]);

    // Infinite scroll — use refs so the callback is stable and doesn't re-trigger onScroll
    const fetchMoreRef = useRef(fetchMore);
    fetchMoreRef.current = fetchMore;
    const dataLengthRef = useRef(data.length);
    dataLengthRef.current = data.length;
    const filterStateRef = useRef(serverFilters);
    filterStateRef.current = serverFilters;

    const loadMore = useCallback(async () => {
        if (!fetchMoreRef.current || filterMode !== 'server') return;
        const sortBy = sorting[0]?.id;
        const sortOrder = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;
        await fetchMoreRef.current({
            variables: {
                offset: dataLengthRef.current,
                sortBy,
                sortOrder,
                filters: filterStateRef.current as Partial<TFilter>,
            },
        });
    }, [filterMode, sorting]);

    const { onScroll, isFetchingMore } = useInfiniteScroll({
        hasMore,
        loadMore: fetchMore ? loadMore : undefined,
    });

    // Build table columns with optional selection and details columns
    const tableColumns = useMemo(() => {
        // In server mode, only columns opted-in via meta.enableServerSort are sortable.
        // In client mode, respect each column's own enableSorting setting.
        const cols: ColumnDef<TData, unknown>[] =
            filterMode === 'server'
                ? columns.map((col) => {
                      const meta = col.meta as DataTableColumnMeta<TFilter> | undefined;
                      return { ...col, enableSorting: meta?.enableServerSort === true };
                  })
                : [...columns];

        if (enableSelection) {
            cols.unshift({
                id: '_select',
                header: ({ table }) => (
                    <Checkbox
                        checked={table.getIsAllPageRowsSelected()}
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Tout sélectionner"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Sélectionner la ligne"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            });
        }

        if (getRowLink) {
            cols.push({
                id: '_details',
                header: 'Détails',
                cell: ({ row }) => {
                    const link = getRowLink(row.original);
                    return link ? (
                        <Link to={link} data-test="more-details">
                            <div className="flex items-center">
                                <IconClipboardList size={22} />
                            </div>
                        </Link>
                    ) : null;
                },
                enableSorting: false,
                enableHiding: false,
            });
        }

        return cols;
    }, [columns, enableSelection, getRowLink, filterMode]);

    const hasFooter = useMemo(() => tableColumns.some((col) => col.footer != null), [tableColumns]);

    // Row selection: controlled when `selectedKeys` is provided (parent owns the set, so programmatic
    // selection and clearing reflect in the table); otherwise an internal uncontrolled state is used.
    const isSelectionControlled = enableSelection && selectedKeys !== undefined;
    const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
    const rowSelection = useMemo<RowSelectionState>(() => {
        if (!isSelectionControlled) return internalRowSelection;
        return (selectedKeys ?? []).reduce<RowSelectionState>((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});
    }, [isSelectionControlled, selectedKeys, internalRowSelection]);
    const handleRowSelectionChange = useCallback(
        (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
            const next = typeof updater === 'function' ? updater(rowSelection) : updater;
            if (!isSelectionControlled) setInternalRowSelection(next);
            onSelectionChange?.(Object.keys(next).filter((id) => next[id]));
        },
        [rowSelection, isSelectionControlled, onSelectionChange],
    );

    // Table instance
    const table = useReactTable({
        data: tableData,
        columns: tableColumns,
        state: {
            sorting,
            columnVisibility,
            columnFilters: filterMode === 'client' ? columnFiltersState : [],
            globalFilter: filterMode === 'client' ? globalFilter : undefined,
            grouping,
            expanded,
            rowSelection,
        },
        meta: { loadingRowIds },
        getRowId: (row) => getRowId(row),
        enableRowSelection: enableSelection,
        onRowSelectionChange: handleRowSelectionChange,
        onSortingChange: handleSortingChange,
        onColumnVisibilityChange: setColumnVisibility,
        onGroupingChange: setGrouping,
        onExpandedChange: setExpanded,
        getCoreRowModel: getCoreRowModel(),
        ...(enableLazyTree
            ? {
                  getSubRows: (row: TData) => (row as TData & { subRows?: TData[] }).subRows,
                  getRowCanExpand: (row: Row<TData>) =>
                      row.depth === 0 && (getRowCanExpandProp?.(row.original) ?? false),
                  // Client search filters only the root rows (by their own value); a matching root keeps all of
                  // its lazily-loaded children. Without this, a child match would surface its parent and the
                  // search would mix entities (e.g. typing a product name in the columns view).
                  maxLeafRowFilterDepth: 0,
              }
            : {}),
        ...(grouping.length > 0 || enableLazyTree
            ? {
                  getExpandedRowModel: getExpandedRowModel(),
                  autoResetExpanded: false,
              }
            : {}),
        ...(grouping.length > 0
            ? {
                  getGroupedRowModel: getGroupedRowModel(),
                  autoResetGrouping: false,
              }
            : {}),
        ...(filterMode === 'client'
            ? {
                  getSortedRowModel: getSortedRowModel(),
                  getFilteredRowModel: getFilteredRowModel(),
              }
            : {
                  manualSorting: true,
                  manualFiltering: true,
              }),
    });

    // When a filter is activated, ensure the corresponding column is visible
    const handleUpdateFilter = useCallback(
        (key: string, value: unknown) => {
            updateFilter(key, value);
            const hasValue =
                value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
            if (hasValue) {
                for (const col of columns) {
                    const meta = col.meta as DataTableColumnMeta<TFilter> | undefined;
                    if (!meta?.filter) continue;
                    const columnId = 'accessorKey' in col ? String(col.accessorKey) : 'id' in col ? String(col.id) : '';
                    const filterKey = meta.filter.filterKey ? String(meta.filter.filterKey) : columnId;
                    if (filterKey === key) {
                        const column = table.getColumn(columnId);
                        if (column && !column.getIsVisible()) {
                            column.toggleVisibility(true);
                        }
                        break;
                    }
                }
            }
        },
        [updateFilter, columns, table],
    );

    // Virtualization: render only the rows near the viewport via top/bottom spacer rows.
    // This keeps the semantic <table> layout intact (auto column sizing + sticky header).
    const { rows } = table.getRowModel();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: () => estimatedRowHeight,
        overscan: 12,
        enabled: enableVirtualization,
    });
    const virtualItems = enableVirtualization ? rowVirtualizer.getVirtualItems() : [];
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const paddingBottom =
        virtualItems.length > 0 ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end : 0;

    const renderDataRow = (row: Row<TData>) => {
        if (row.getIsGrouped() && renderGroupRow) {
            return (
                <TableRow key={row.id} data-grouped="true">
                    {renderGroupRow({
                        row,
                        // getLeafRows() also returns intermediate grouped rows — keep actual leaves only,
                        // so consumers can count/aggregate them directly.
                        leafRows: row.getLeafRows().filter((leaf) => !leaf.getIsGrouped()),
                        columnCount: row.getVisibleCells().length,
                    })}
                </TableRow>
            );
        }
        const isHighlighted = highlightedKeys?.includes(row.id);
        const details = renderRowDetails?.(row.original) ?? null;
        return (
            <Fragment key={row.id}>
                <TableRow
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    className={cn(
                        onRowClick && 'cursor-pointer',
                        isHighlighted && 'text-primary',
                        getRowClassName?.(row.original),
                    )}
                    onClick={() => onRowClick?.(row.original)}
                >
                    {row.getVisibleCells().map((cell) => (
                        <TableCell
                            key={cell.id}
                            className={cn(
                                // Pin the action column to the right so it stays reachable when the table overflows.
                                cell.column.id === '_details' && 'sticky right-0 z-10 border-l bg-background',
                            )}
                        >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                    ))}
                </TableRow>
                {details && (
                    <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={row.getVisibleCells().length} className="p-0 whitespace-normal">
                            <div className="sticky left-0 w-[100cqw] p-2">{details}</div>
                        </TableCell>
                    </TableRow>
                )}
            </Fragment>
        );
    };

    const toolbar = (
        <DataTableToolbar<TData, TFilter>
            table={table}
            columns={columns}
            searchValue={searchValue}
            onSearchChange={updateSearch}
            searchPlaceholder={searchPlaceholder}
            autoFocusSearch={autoFocusSearch}
            filterState={filterState}
            updateFilter={handleUpdateFilter}
            clearFilters={clearFilters}
            enableColumnVisibility={!disableColumnVisibility}
            toolbarActions={toolbarActions}
            extraFilters={extraFilters}
            onEnter={() => {
                const firstRow = table.getRowModel().rows[0];
                const link = firstRow && getRowLink?.(firstRow.original);
                if (link) navigate(link);
            }}
        />
    );

    return (
        <div className="flex h-full w-full flex-col p-2 overflow-hidden">
            {/* Default: toolbar fixed above the scroll. With a banner it moves inside the scroll (below the banner). */}
            {!scrollableBanner && toolbar}

            <div
                ref={scrollContainerRef}
                className={cn(
                    // Only the table's own container defers to this scroll: tables nested in row details keep theirs.
                    'relative flex-1 overflow-auto [&>[data-slot=table-container]]:overflow-visible',
                    // Row details size themselves on this container's width (cqw).
                    renderRowDetails && '@container',
                )}
                onScroll={onScroll}
            >
                {scrollableBanner && (
                    <>
                        {scrollableBanner}
                        {toolbar}
                    </>
                )}
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className={cn(
                                            'sticky top-0 z-10 bg-background',
                                            // Keep the action column header aligned with its pinned cells.
                                            header.column.id === '_details' && 'right-0 z-20 border-l',
                                        )}
                                    >
                                        {header.isPlaceholder ? null : header.column.getCanSort() ? (
                                            <DataTableColumnHeader
                                                column={header.column}
                                                title={
                                                    typeof header.column.columnDef.header === 'string'
                                                        ? header.column.columnDef.header
                                                        : header.id
                                                }
                                            />
                                        ) : (
                                            flexRender(header.column.columnDef.header, header.getContext())
                                        )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {rows.length > 0 ? (
                            enableVirtualization ? (
                                <>
                                    {paddingTop > 0 && (
                                        <tr aria-hidden="true">
                                            <td style={{ height: paddingTop }} colSpan={tableColumns.length} />
                                        </tr>
                                    )}
                                    {virtualItems.map((virtualItem) => renderDataRow(rows[virtualItem.index]))}
                                    {paddingBottom > 0 && (
                                        <tr aria-hidden="true">
                                            <td style={{ height: paddingBottom }} colSpan={tableColumns.length} />
                                        </tr>
                                    )}
                                </>
                            ) : (
                                rows.map(renderDataRow)
                            )
                        ) : (
                            <TableRow>
                                <TableCell colSpan={tableColumns.length} className="h-24 text-center">
                                    {emptyMessage}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                    {hasFooter && rows.length > 0 && (
                        <TableFooter>
                            {table.getFooterGroups().map((footerGroup) => (
                                <TableRow key={footerGroup.id}>
                                    {footerGroup.headers.map((header) => (
                                        <TableCell
                                            key={header.id}
                                            className={cn(
                                                'sticky bottom-0 z-10 bg-background',
                                                // Keep the action column footer aligned with its pinned cells.
                                                header.column.id === '_details' && 'right-0 z-20 border-l',
                                            )}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.footer, header.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableFooter>
                    )}
                </Table>

                {isFetchingMore && (
                    <div className="flex justify-center py-4">
                        <Loader mode={LoaderModeType.Spin} />
                    </div>
                )}

                {/* Suppress the center overlay while a fetch-more is in flight — the bottom loader covers that
                    case. With notifyOnNetworkStatusChange, Apollo's `loading` also flips true during fetchMore,
                    which would otherwise show both loaders at once on scroll. */}
                {isLoading && !isFetchingMore && <DataTableLoadingOverlay />}
            </div>
        </div>
    );
}
